import React, { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import PermContactCalendarIcon from "@material-ui/icons/PermContactCalendar";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
// import AsyncSelect from "../AsyncSelect";
import QueueSelectSingle from "../QueueSelectSingle";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		borderRadius: 8,
		background: theme.mode === "light" ? "#ffffff" : "#2f2f2f",
		color: theme.mode === "light" ? "#172033" : "#f8fafc",
		boxShadow: theme.mode === "light" ? "0 24px 60px rgba(15, 23, 42, 0.18)" : "0 24px 60px rgba(0,0,0,0.42)",
		overflow: "hidden",
	},
	dialogTitle: {
		padding: theme.spacing(2, 2.5),
		background: theme.mode === "light" ? "linear-gradient(135deg, #ffffff 0%, #edf6ff 100%)" : "#343434",
		borderBottom: theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)",
	},
	titleRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
	},
	titleIcon: {
		width: 42,
		height: 42,
		borderRadius: 8,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: theme.palette.primary.main,
		color: "#ffffff",
		boxShadow: theme.mode === "light" ? "0 10px 22px rgba(93, 170, 252, 0.22)" : "0 10px 22px rgba(0,0,0,0.24)",
	},
	titleEyebrow: {
		fontSize: 11,
		fontWeight: 800,
		textTransform: "uppercase",
		letterSpacing: 0.5,
		color: theme.mode === "light" ? "#315b8f" : "#9ccaef",
	},
	titleText: {
		fontSize: 22,
		lineHeight: 1.1,
		fontWeight: 900,
		color: theme.mode === "light" ? "#0f172a" : "#f8fafc",
	},
	dialogContent: {
		padding: theme.spacing(2.25),
		background: theme.mode === "light" ? "#f4f7fb" : "#424242",
		borderTop: 0,
		borderBottom: 0,
		"& .MuiInputLabel-root": {
			color: theme.mode === "light" ? "#64748b" : "#cbd5e1",
		},
	},
	sectionBlock: {
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1.5),
		borderRadius: 8,
		background: theme.mode === "light" ? "#ffffff" : "#2f2f2f",
		border: theme.mode === "light" ? "1px solid #d9e2ef" : "1px solid rgba(255,255,255,0.08)",
		boxShadow: theme.mode === "light" ? "0 8px 18px rgba(15, 23, 42, 0.04)" : "0 10px 24px rgba(0,0,0,0.14)",
	},
	sectionTitle: {
		marginBottom: theme.spacing(1),
		fontSize: 13,
		fontWeight: 900,
		letterSpacing: 0.2,
		textTransform: "uppercase",
		color: theme.mode === "light" ? "#315b8f" : "#9ccaef",
	},
	fieldRow: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(1.25),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	textField: {
		flex: 1,
		marginTop: theme.spacing(0.75),
		marginBottom: theme.spacing(0.75),
		"& .MuiOutlinedInput-root": {
			borderRadius: 8,
			backgroundColor: theme.mode === "light" ? "#ffffff" : "#343434",
			color: theme.mode === "light" ? "#172033" : "#f8fafc",
			"& fieldset": {
				borderColor: theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.12)",
			},
			"&:hover fieldset": {
				borderColor: theme.mode === "light" ? "#c6d4e6" : "rgba(255,255,255,0.20)",
			},
			"&.Mui-focused fieldset": {
				borderColor: theme.palette.primary.main,
				borderWidth: 1,
				boxShadow: theme.mode === "light" ? "0 0 0 3px rgba(93, 170, 252, 0.12)" : "0 0 0 3px rgba(93, 170, 252, 0.14)",
			},
		},
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		gap: theme.spacing(1),
		[theme.breakpoints.down("xs")]: {
			alignItems: "stretch",
			flexDirection: "column",
		},
	},
	infoRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		color: theme.mode === "light" ? "#334155" : "#dbeafe",
		"& .MuiTypography-root": {
			fontSize: 13,
		},
	},
	addExtraButton: {
		flex: 1,
		marginTop: theme.spacing(1),
		borderRadius: 8,
		fontWeight: 800,
		borderColor: theme.mode === "light" ? "#bfdbfe" : "rgba(156,202,239,0.22)",
		backgroundColor: theme.mode === "light" ? "#edf6ff" : "#263f50",
		color: theme.mode === "light" ? theme.palette.primary.main : "#dbeafe",
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#e5f2ff" : "#2e5065",
			borderColor: theme.palette.primary.main,
		},
	},
	dialogActions: {
		padding: theme.spacing(1.5, 2.5),
		background: theme.mode === "light" ? "#ffffff" : "#2f2f2f",
		borderTop: theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)",
	},
	cancelButton: {
		borderRadius: 8,
		fontWeight: 800,
		textTransform: "uppercase",
		color: theme.mode === "light" ? "#64748b" : "#dbeafe",
		borderColor: theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.14)",
	},
	saveButton: {
		borderRadius: 8,
		fontWeight: 800,
		textTransform: "uppercase",
		boxShadow: theme.mode === "light" ? "0 10px 22px rgba(93, 170, 252, 0.22)" : "0 10px 22px rgba(0,0,0,0.24)",
	},
	deleteButton: {
		color: "#ff3860",
		backgroundColor: theme.mode === "light" ? "#fff6f8" : "rgba(255, 56, 96, 0.08)",
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#ffe8ee" : "rgba(255, 56, 96, 0.14)",
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

        const initialState = {
                name: "",
                number: "",
                email: "",
                disableBot: false,
                lgpdAcceptedAt: "",
                queueId: null
        };

	const [contact, setContact] = useState(initialState);
	const [disableBot, setDisableBot] = useState(false);
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact(data);
					setDisableBot(data.disableBot)
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
                try {
                        const queueId = values.queueId || null;
                        if (contactId) {
                                await api.put(`/contacts/${contactId}`, { ...values, queueId, disableBot: disableBot });
                                handleClose();
                        } else {
                                const { data } = await api.post("/contacts", { ...values, queueId, disableBot: disableBot });
                                if (onSave) {
                                        onSave(data);
                                }
                                handleClose();
                        }
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
				classes={{ paper: classes.dialogPaper }}
			>
				<DialogTitle id="form-dialog-title" disableTypography className={classes.dialogTitle}>
					<div className={classes.titleRow}>
						<div className={classes.titleIcon}>
							<PermContactCalendarIcon />
						</div>
						<div>
							<Typography className={classes.titleEyebrow}>
								Cadastro do contato
							</Typography>
							<Typography className={classes.titleText}>
								{contactId
									? `${i18n.t("contactModal.title.edit")}`
									: `${i18n.t("contactModal.title.add")}`}
							</Typography>
						</div>
					</div>
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers className={classes.dialogContent}>
								<Typography className={classes.sectionTitle}>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									fullWidth
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.number")}
									name="number"
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5513912344321"
									variant="outlined"
									margin="dense"
									fullWidth
									className={classes.textField}
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
										className={classes.textField}
									/>
								</div>
								<div>
                                                                <TagsContainer contact={contact} className={classes.textField} />
                                                                </div>
                                                                <div>
                                                                        <QueueSelectSingle />
                                                                </div>
								<Typography className={classes.infoRow}>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() =>
											setDisableBot(!disableBot)
										}
										name="disableBot"
										color="primary"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								<Typography className={classes.infoRow}>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography className={classes.infoRow}>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : ""}
								</Typography>

								{/* <Typography variant="subtitle1" gutterBottom>{i18n.t("contactModal.form.customer_portfolio")}</Typography> */}
								{/* <div style={{ marginTop: 10 }}>
									<AsyncSelect url="/users" dictKey={"users"}
										initialValue={values.user} width="100%" label={i18n.t("contactModal.form.attendant")}
										onChange={(event, value) => setFieldValue("userId", value ? value.id : null)} />
								</div>
								<div style={{ marginTop: 10 }}>
									<AsyncSelect url="/queue" dictKey={null}
										initialValue={values.queue} width="100%" label={i18n.t("contactModal.form.queue")}
										onChange={(event, value) => setFieldValue("queueId", value ? value.id : null)} />
								</div> */}
								<Typography className={classes.sectionTitle}>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
															className={classes.deleteButton}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													className={classes.addExtraButton}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									type="button"
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
									className={classes.cancelButton}
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={`${classes.btnWrapper} ${classes.saveButton}`}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
